import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VerificationStatus } from '../../@types/VerificationStatus';
import VerificationStatusCheck from '../../components/verificationStatusCheck/VerificationStatusCheck';
import { VerificationMethod } from '../../@types/VerificationMethod';
import { trackEvent } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import {
  getFinapiVerificationStatus,
  getFinapiVerificationStatusLightRegisteredUser,
} from '../../api/finapi/FinApiVerificationAttemptApi';
import { FinApiStatus, getErrorTranslationKey } from '../../api/finapi/model/FinApiStatus';
import { VerificationCompletionData } from '../../@types/VerificationCompletionData';
import { useTvsState } from '../../utils/useTvsState';

const POLLING_INTERVAL_MS = 1000;
const MAX_VERIFICATION_TIME_MS = 5000;
const PROGRESS_INCREMENT = 20;
const MAX_PROGRESS_WHILE_CHECKING = 100;
const COMPLETION_DELAY_MS = 1000; // 1 seconds delay to show 100% progress

interface FinapiStatusCheckPageProps {
  onVerificationComplete?: (status: VerificationStatus, data: VerificationCompletionData) => void;
  onError: (error: string) => void;
}

const FinapiStatusCheckPage: React.FC<FinapiStatusCheckPageProps> = ({ onVerificationComplete, onError }) => {
  const [progress, setProgress] = useState(0);
  const [isChecking, setIsChecking] = useState(true);
  const [searchParams] = useSearchParams();
  const [, setTvsState] = useTvsState();
  const { clientId, hideToasterOnSuccess, userId } = useClientConfig();
  const sessionId = searchParams.get('tvs_session_id');

  useEffect(() => {
    let verificationTimer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;
    let timeoutTimer: NodeJS.Timeout;
    let completionTimer: NodeJS.Timeout;

    const handleVerificationSuccess = () => {
      setProgress(100);
      cleanup();
      completionTimer = setTimeout(() => {
        setIsChecking(false);
        trackEvent('is24.de.sicherheit.verifizierung.finapi', 'verification_successful', 'user', clientId, 'finapi');
        if (hideToasterOnSuccess) {
          setTvsState('');
        } else {
          setTvsState('verification_success');
        }
        onVerificationComplete?.(VerificationStatus.SUCCESS, {
          sessionId: sessionId || '',
          verificationMethod: VerificationMethod.Finapi,
        });
      }, COMPLETION_DELAY_MS);
    };

    const handleVerificationFailure = (errorKey: string, skipDelay = false) => {
      onError(errorKey);
      setProgress(100);
      cleanup();

      if (skipDelay) {
        redirectToFinapiFailure();
      } else {
        completionTimer = setTimeout(() => {
          redirectToFinapiFailure();
        }, COMPLETION_DELAY_MS);
      }
    };

    const redirectToFinapiFailure = () => {
      setIsChecking(false);
      setTvsState('verification_failure_finapi_check');
      onVerificationComplete?.(VerificationStatus.FAILED, {
        sessionId: sessionId || '',
        verificationMethod: VerificationMethod.Finapi,
      });
    };

    const cleanup = () => {
      clearTimeout(timeoutTimer);
      clearInterval(progressTimer);
      clearInterval(verificationTimer);
    };

    if (!sessionId) {
      const errorKey = getErrorTranslationKey(null);
      handleVerificationFailure(errorKey);
      return;
    }

    const checkVerificationStatus = async () => {
      try {
        const response: FinApiStatus = userId
          ? await getFinapiVerificationStatusLightRegisteredUser(sessionId, userId)
          : await getFinapiVerificationStatus(sessionId);

        if (response.isVerified) {
          handleVerificationSuccess();
        } else if (response.errorReason) {
          const errorKey = getErrorTranslationKey(response.errorReason);
          handleVerificationFailure(errorKey);
        }
      } catch (error) {
        console.error('Verification check error, continuing to check...', error);
      }
    };

    const updateProgress = () => {
      setProgress((prevProgress) => Math.min(prevProgress + PROGRESS_INCREMENT, MAX_PROGRESS_WHILE_CHECKING));
    };

    if (isChecking) {
      timeoutTimer = setTimeout(() => {
        const errorKey = getErrorTranslationKey(null);
        handleVerificationFailure(errorKey, true); // Pass true to skip delay on timeout
      }, MAX_VERIFICATION_TIME_MS);

      verificationTimer = setInterval(checkVerificationStatus, POLLING_INTERVAL_MS);
      progressTimer = setInterval(updateProgress, POLLING_INTERVAL_MS);
      checkVerificationStatus();

      return () => {
        cleanup();
        clearTimeout(completionTimer);
      };
    }
  }, [isChecking, onVerificationComplete, onError, clientId, hideToasterOnSuccess, sessionId, userId]);

  if (!isChecking) return null;

  return <VerificationStatusCheck progress={progress} verificationMethod={VerificationMethod.Finapi} />;
};

export default FinapiStatusCheckPage;
